import {
	dxtToLocalServerTime,
	getMomentTz,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Grid2 as Grid } from "@mui/material";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGridStyles } from "../../styles/gridStyles";
import { LadleStatus } from "../models/LadleStatus";
import { StatusBarReference } from "./status-bar-ag-grid";
import { StatusIndicator } from "./status-indicator";

type Props = {
	ladleStatus: LadleStatus[];
};

export const TableLadleStatus = ({ ladleStatus }: Props) => {
	const gridRef = useRef<AgGridReact>(null);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const classes = useGridStyles();
	const { state } = useUTLSettingsContext();

	const MAX_HEAT_COUNTS = get(state, "LADLE_STATUS_MAX_HEAT_COUNTS", 100);
	const MAX_CONTACT_TIME = get(state, "LADLE_STATUS_MAX_CONTACT_TIME", 10000);

	const getStatusData = (params: any) => {
		if (!params.data.LastTappedDateTime) return { lastTappedDate: null };

		const now = getMomentTz()();
		const lastTapped = getMomentTz()(params.data.LastTappedDateTime);
		const diffDays = now.diff(lastTapped, "days");

		return {
			lastTappedDate: params.data.LastTappedDateTime,
			diffDays: diffDays,
		};
	};

	const formatContactTime = (params: any) => {
		if (!params.value || params.value === null) return "";

		const totalMinutes = Number(params.value);
		if (isNaN(totalMinutes)) return "";

		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;

		return `${hours}:${minutes.toString().padStart(2, "0")}`;
	};

	const calculateDaysDifference = (params: any) => {
		const { FirstHeatDateTime, LastTappedDateTime } = params.data;

		if (!FirstHeatDateTime || !LastTappedDateTime) return "";

		const firstHeat = getMomentTz()(FirstHeatDateTime);
		const lastTapped = getMomentTz()(LastTappedDateTime);

		if (!firstHeat.isValid() || !lastTapped.isValid()) return "";

		const diffDays = lastTapped.diff(firstHeat, "days");

		return diffDays >= 0 ? diffDays.toString() : "";
	};
	const frameworkComponents = {
		statusBarReference: StatusBarReference,
	};
	const statusBar = {
		statusPanels: [
			{
				statusPanel: "statusBarReference",
				align: "left",
			},
		],
	};
	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
		};
	}, []);

	useEffect(() => {
		setColumnDefs([
			{
				field: "status",
				headerName: "Status",
				minWidth: 70,
				maxWidth: 70,
				cellRenderer: StatusIndicator,
				valueGetter: getStatusData,
				cellStyle: {
					padding: "0px",
					textAlign: "center",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				},
			},
			{
				field: "LadleName",
				headerName: "Ladle",
				minWidth: 100,
				maxWidth: 100,
				flex: 1,
			},
			{
				field: "HeatCount",
				headerName: "Total Heats",
				cellClassRules: {
					"max-limit": (params) => {
						return Number(params.value) > MAX_HEAT_COUNTS;
					},
				},
				minWidth: 100,
				flex: 1,
			},
			{
				field: "TotalContactTime",
				headerName: "Total Contact [hhh:mm]",
				minWidth: 180,
				flex: 1,
				wrapHeaderText: true,
				valueFormatter: formatContactTime,
				cellClassRules: {
					"max-limit": (params) => {
						return Number(params.value) > MAX_CONTACT_TIME;
					},
				},
				headerStyle: {
					textAlign: "center",
				},
			},
			{
				field: "FirstHeatName",
				headerName: "First Heat Number",
				minWidth: 140,
				flex: 1,
			},
			{
				field: "LastTappedHeatName",
				headerName: "Last Tapped Heat Number",
				minWidth: 200,
				flex: 1,
			},
			{
				field: "FirstHeatDateTime",
				headerName: "First Heat Date",
				minWidth: 140,
				flex: 1,
				valueFormatter: (params) =>
					dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm:ss"),
			},
			{
				field: "LastTappedDateTime",
				headerName: "Last Tap Date",
				minWidth: 140,
				flex: 1,
				valueFormatter: (params) =>
					dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm:ss"),
			},
			{
				field: "days",
				headerName: "Days",
				minWidth: 80,
				flex: 1,
				valueGetter: calculateDaysDifference,
				cellStyle: {
					textAlign: "center",
				},
			},
		]);
	}, []);

	return (
		<Grid container style={{ width: "100%", display: "flex" }}>
			<Grid
				size={{ xs: 12 }}
				style={{
					height: "78vh",
					minHeight: "37vh",
					width: "100%",
					border: "1px solid #e0e0e0",
					margin: "20px 0 10px",
				}}
			>
				<AgGridReact
					ref={gridRef}
					rowData={ladleStatus}
					columnDefs={columnDefs}
					rowHeight={33}
					headerHeight={32}
					animateRows={true}
					suppressContextMenu={true}
					suppressRowHoverHighlight={true}
					statusBar={statusBar}
					components={frameworkComponents}
				/>
			</Grid>
		</Grid>
	);
};
